import { useState, useEffect, useCallback, useMemo } from 'react';
import { disbursementService } from 'api/services/disbursementService';
import type {
  ID, PaginatedResponse, Disbursement,
  CreateDisbursementRequest, UpdateDisbursementRequest, DisbursementSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useDisbursements = (params?: DisbursementSearchRequest) => {
  const [data, setData] = useState<Disbursement[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Disbursement>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchDisbursements = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await disbursementService.getAll(params);
      setData(r.data); setPagination(r.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchDisbursements(); }, [fetchDisbursements]);
  return { data, pagination, loading, error, refetch: fetchDisbursements };
};

export const useDisbursement = (id: ID | null | undefined) => {
  const [data, setData] = useState<Disbursement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { const r = await disbursementService.getById(id); setData(r.data); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

export const useCreateDisbursement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const createDisbursement = async (data: CreateDisbursementRequest, options?: { onSuccess?: (item: Disbursement) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await disbursementService.create(data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { createDisbursement, loading, error };
};

export const useUpdateDisbursement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const updateDisbursement = async (id: ID, data: UpdateDisbursementRequest, options?: { onSuccess?: (item: Disbursement) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await disbursementService.update(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { updateDisbursement, loading, error };
};

export const useDeleteDisbursement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const deleteDisbursement = async (id: ID, options?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { await disbursementService.delete(id); options?.onSuccess?.(); }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { deleteDisbursement, loading, error };
};
